import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';
import type { ConfigRendererProps } from './types';

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'ap_invoice', label: 'AP Invoice' },
  { value: 'ap_invoice_update', label: 'AP Invoice Update' },
] as const;

export function ClassifyDocumentConfigRenderer({
  config,
  onConfigChange,
}: ConfigRendererProps) {
  const currentDocumentTypes = Array.isArray(config.documentTypes)
    ? (config.documentTypes as string[])
    : ['purchase_order']; // Default to purchase_order for backward compatibility

  const handleToggleDocumentType = (value: string, checked: boolean) => {
    let updatedTypes: string[];

    if (checked) {
      // Add the type if it's not already in the array
      updatedTypes = currentDocumentTypes.includes(value)
        ? currentDocumentTypes
        : [...currentDocumentTypes, value];
    } else {
      // Remove the type, but prevent removing the last one
      updatedTypes = currentDocumentTypes.filter((type) => type !== value);
      // At least one document type must be selected
      if (updatedTypes.length === 0) {
        return;
      }
    }

    onConfigChange({
      ...config,
      documentTypes: updatedTypes,
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Document Types</Label>
          <p className="text-xs text-muted-foreground">
            Select which document types to classify. OTHER is always included as
            a fallback.
          </p>
        </div>

        <div className="space-y-2 rounded-md border p-3">
          {DOCUMENT_TYPE_OPTIONS.map((option) => {
            const isChecked = currentDocumentTypes.includes(option.value);
            const isLastSelected =
              isChecked && currentDocumentTypes.length === 1;

            return (
              <div key={option.value} className="flex items-center gap-3">
                <Checkbox
                  id={`doc-type-${option.value}`}
                  checked={isChecked}
                  disabled={isLastSelected}
                  onCheckedChange={(checked) =>
                    handleToggleDocumentType(
                      option.value,
                      checked === true || checked === 'indeterminate',
                    )
                  }
                />
                <Label
                  htmlFor={`doc-type-${option.value}`}
                  className={
                    isLastSelected
                      ? 'text-sm font-normal text-muted-foreground'
                      : 'cursor-pointer text-sm font-normal'
                  }
                >
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-muted-foreground">
          At least one document type must be selected. Documents will be
          classified into one of the selected types or OTHER if they don't match
          any selected type.
        </p>
      </div>
    </div>
  );
}
